/**
 * @copyright Copyright 2023-2024 Epic Systems Corporation
 * @file Modal for collecting download fields
 * @module Epic.AppOrchard.Core.TokenDownloadModal
 */

import {
	Button,
	Divider,
	Flex,
	FormControl,
	FormLabel,
	Icon,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	NumberInput,
	NumberInputField,
	SimpleGrid,
	Spacer,
	Switch,
	Text,
} from "@chakra-ui/react";
import { AOTooltip, ErrorMessage } from "ao/components/Core";
import { ConsumerTypes } from "ao/data";
import { MaxIntValue } from "ao/data/constants";
import { IShowroomAppDownloadModel } from "ao/types/showroom";
import React, { FC, memo, useEffect, useState } from "react";
import { BsFillQuestionCircleFill } from "react-icons/bs";

interface IProps {
	isOpen: boolean;
	onClose: () => void;
	appDetails: IShowroomAppDownloadModel;
	setAppDetails: (x: IShowroomAppDownloadModel) => void;
	onAccept: () => void;
}

export const EditTokensModal: FC<IProps> = memo((props: IProps) => {
	const { isOpen, onClose, appDetails, setAppDetails, onAccept } = props;

	const appModel = appDetails.appModel;

	const [shouldResetFields, setShouldResetFields] = useState<boolean>(true);

	/** resets fields whenever the modal is reopened  */
	useEffect(() => {
		if (isOpen && shouldResetFields) {
			setAppDetails(resetTokensToDefault(appDetails));
			setShouldResetFields(false);
		} else if (!isOpen) {
			setShouldResetFields(true);
		}
	}, [appDetails, shouldResetFields, isOpen, setAppDetails]);

	const error =
		appDetails.accessTokenValidity! < 1 ||
		(appModel.refreshTokensRequired && appDetails.refreshTokenValidity! < 1)
			? "Enter a positive number for token validity to proceed."
			: "";

	const frontendNoCredentials =
		appModel.consumerType !== ConsumerTypes.Backend && !appModel.isConfidentialClient;
	const credentialsNonRefresh =
		(appModel.consumerType === ConsumerTypes.Backend || appModel.isConfidentialClient) &&
		!appModel.refreshTokensRequired;

	let blurb = "";

	if (frontendNoCredentials) {
		if (appDetails.isDownloaded) {
			blurb =
				"This app uses OAuth 2.0. You can override the app's access tokens validity duration by entering a new duration (in minutes). Once you click 'Update' the new token validity duration will be set for this client ID. Once set, newly issued tokens will respect these settings.";
		} else {
			blurb = appModel.isEpicOnFhirApp
				? "This app uses OAuth 2.0. You can override the duration that access tokens are valid for this app. Once you click the 'Download' the app's client IDs will be downloaded to your environment."
				: "This app uses OAuth 2.0. You can override the duration that access tokens are valid for this app. Once you click the 'Request Client ID' button a request will be sent to the vendor to enable the app's client IDs for your system.";
		}
	} else if (credentialsNonRefresh) {
		if (appDetails.isDownloaded) {
			blurb =
				"This app uses OAuth 2.0. You can override the app's access tokens validity duration by entering a new duration (in minutes). Once you click 'Update' the new token validity duration will be set for this client ID. Once set, newly issued tokens will respect these settings.";
		} else {
			blurb =
				"This app uses OAuth 2.0. You can override the duration that access tokens are valid for this app. Once you click the 'Request Client ID' button a request will be sent to the vendor to enable the app's client IDs for your system.";
		}
	} else if (appModel.refreshTokensRequired) {
		if (appDetails.isDownloaded) {
			blurb =
				"This app uses OAuth 2.0 with refresh tokens. You can override the app's access tokens validity duration by entering a new duration (in minutes) and override the refresh token validity duration by entering a new duration (in days). Once you click 'Update' the new token validity duration will be set for this client ID. Once set the new tokens issued will respect these settings.";
		} else {
			blurb =
				"This app uses OAuth 2.0 with refresh tokens. You can override the duration that refresh and access tokens are valid for this app. Once you click the 'Request Client ID' button a request will be sent to the vendor to enable the app's client IDs for your system.";
		}
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="lg" preserveScrollBarGap isCentered returnFocusOnClose>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Token Validity</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<SimpleGrid spacing={8}>
						<Text>{blurb}</Text>

						<FormControl isInvalid={appDetails.accessTokenValidity < 1}>
							<FormLabel>
								Access token validity (minutes){" "}
								<AOTooltip helptext="Access tokens are obtained by an app as part of the OAuth 2.0 authentication process and allow apps to access information from your system for a defined period of time.">
									<Icon as={BsFillQuestionCircleFill} boxSize={"4"} mb={"1"}></Icon>
								</AOTooltip>
							</FormLabel>
							<NumberInput
								max={MaxIntValue}
								value={appDetails.accessTokenValidity}
								onChange={(e) => {
									let val = Number(e);
									if (!isNaN(val) && val <= MaxIntValue) {
										setAppDetails({ ...appDetails, accessTokenValidity: val });
									}
								}}
							>
								<NumberInputField />
							</NumberInput>
						</FormControl>
						{appModel.refreshTokensRequired && (
							<FormControl isInvalid={appDetails.refreshTokenValidity < 1}>
								<FormLabel>
									Refresh token validity (days){" "}
									<AOTooltip helptext="Refresh tokens are used to allow apps to continue to access information from your system for a defined period of time after the initial access token has expired without requiring a user or patient to authorize the app again. Refresh tokens are typically used for apps that are not embedded within Epic.">
										<Icon as={BsFillQuestionCircleFill} boxSize={"4"} mb={"1"}></Icon>
									</AOTooltip>
								</FormLabel>
								<NumberInput
									max={MaxIntValue}
									value={appDetails.refreshTokenValidity}
									onChange={(e) => {
										let val = Number(e);
										if (!isNaN(val) && val <= MaxIntValue) {
											setAppDetails({ ...appDetails, refreshTokenValidity: val });
										}
									}}
								>
									<NumberInputField />
								</NumberInput>
							</FormControl>
						)}
						{appModel.hasIndefiniteAccess && (
							<FormControl isRequired>
								<FormLabel>
									This app is capable of obtaining indefinite access after initial authorization to data from your system using refresh tokens. You can
									enable or disable indefinite access for the app below.
								</FormLabel>
								<Switch
									isChecked={appDetails.disableIndefiniteAccess ?? false}
									onChange={(e) =>
										setAppDetails({
											...appDetails,
											disableIndefiniteAccess: e.target.checked,
										})
									}
									isFocusable
								>
									Allow indefinite access
								</Switch>
							</FormControl>
						)}
					</SimpleGrid>
				</ModalBody>
				<Divider />
				<ModalFooter display="block" pb="1em" pt="1em">
					<ErrorMessage message={error} isNotForm />
					<Flex my="3px">
						<Spacer />
						<Button
							colorScheme="blue"
							onClick={onAccept}
							mr="2"
							disabled={!!error}
							title={
								appDetails.isDownloaded
									? "Update"
									: "Enter positive numbers for token validity to proceed."
							}
						>
							{appDetails.isDownloaded ? "Update" : "Request Client ID"}
						</Button>
						<Button onClick={onClose}>Cancel</Button>
					</Flex>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
});

export function setDefaultTokens(appDetails: IShowroomAppDownloadModel): IShowroomAppDownloadModel {
	return {
		...appDetails,
		defaultAccessTokenValidity: appDetails.accessTokenValidity,
		defaultRefreshTokenValidity: appDetails.refreshTokenValidity,
		defaultDisableIndefiniteAccess: appDetails.disableIndefiniteAccess ?? false,
	};
}

export function resetTokensToDefault(appDetails: IShowroomAppDownloadModel): IShowroomAppDownloadModel {
	if (
		appDetails.defaultAccessTokenValidity === undefined &&
		appDetails.defaultRefreshTokenValidity === undefined &&
		appDetails.defaultDisableIndefiniteAccess === undefined
	) {
		appDetails = setDefaultTokens(appDetails);
	}
	return {
		...appDetails,
		accessTokenValidity: appDetails.defaultAccessTokenValidity ?? appDetails.accessTokenValidity,
		refreshTokenValidity: appDetails.defaultRefreshTokenValidity ?? appDetails.refreshTokenValidity,
		disableIndefiniteAccess:
			appDetails.defaultDisableIndefiniteAccess ?? appDetails.disableIndefiniteAccess,
	};
}
